import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import ButtonComponent from "../ui/ButtonComponent";
import { auth } from "../../firebase-config";
import SignatureCanvas from "react-signature-canvas";



// Prefer env var, fallback to default CF URL pattern
const CHECKIN_RSVP_URL =
  process.env.REACT_APP_CHECKIN_RSVP_URL ||
  "https://checkinrsvp-774073159618.us-central1.run.app/checkinRsvp";

function useQueryParams() {
  const location = useLocation();
  return new URLSearchParams(location.search);
}

function formatDateTime(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function statusColor(stepDone) {
  return stepDone ? "success" : "default";
}

const CheckinPage = () => {
  const query = useQueryParams();
  const orgId = query.get("org") || "";
  const eventId = query.get("event") || "";
  const rsvpId = query.get("rsvp") || "";
  const code = query.get("code") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const [attendee, setAttendee] = useState(null);
  const [checkIn, setCheckIn] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);

  // Toys signature fields
  const [signatureName, setSignatureName] = useState("");
  const [signatureNote, setSignatureNote] = useState("");
  const sigPadRef = useRef(null);

  const hasRequiredParams = orgId && eventId && rsvpId && code;

  const loadRsvp = useCallback(async () => {
    if (!hasRequiredParams) {
      setError("Missing QR parameters. Please scan a valid event QR code.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setInfoMessage("");

    try {
      const url = `${CHECKIN_RSVP_URL}?org=${encodeURIComponent(
        orgId
      )}&event=${encodeURIComponent(eventId)}&rsvp=${encodeURIComponent(
        rsvpId
      )}&code=${encodeURIComponent(code)}`;

      const resp = await fetch(url, {
        method: "GET",
      });

      const data = await resp.json();

      if (!resp.ok || !data.ok) {
        setError(
          data?.error ||
            "Unable to load RSVP information. The code may be invalid."
        );
        setLoading(false);
        return;
      }

      setAttendee(data.attendee || null);
      setCheckIn(data.checkIn || {});
      setNextStep(data.nextStep || null);
      setEventInfo(data.event || null);

      if (!data.nextStep) {
        setInfoMessage("All check-in steps for this RSVP are complete.");
      }
    } catch (err) {
      console.error("[CheckinPage] loadRsvp error:", err);
      setError("An unexpected error occurred while loading RSVP details.");
    } finally {
      setLoading(false);
    }
  }, [hasRequiredParams, orgId, eventId, rsvpId, code]);

  useEffect(() => {
    loadRsvp();
  }, [loadRsvp]);

  const handleAdvanceStep = async (forcedAction) => {
    if (!hasRequiredParams) return;
    const action = forcedAction || nextStep;
    if (!action) return;

    setSubmitting(true);
    setError("");
    setInfoMessage("");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("You must be signed in to record check-in actions.");
        setSubmitting(false);
        return;
      }

      const idToken = await currentUser.getIdToken();

      const url = `${CHECKIN_RSVP_URL}?org=${encodeURIComponent(
        orgId
      )}&event=${encodeURIComponent(eventId)}&rsvp=${encodeURIComponent(
        rsvpId
      )}&code=${encodeURIComponent(code)}`;

      // Capture signature image only when doing the "toys" step
    let signatureImage;
    if (action === "toys" && sigPadRef.current && !sigPadRef.current.isEmpty()) {
      // Avoid getTrimmedCanvas() because it depends on trim-canvas (import issue in bundle)
      signatureImage = sigPadRef.current
        .getCanvas()
        .toDataURL("image/png");
    }

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          org: orgId,
          event: eventId,
          rsvp: rsvpId,
          code,
          action,
          signatureName:
            action === "toys" && signatureName
              ? signatureName.trim()
              : undefined,
          signatureNote:
            action === "toys" && signatureNote
              ? signatureNote.trim()
              : undefined,
          signatureImage, // base64 PNG or undefined
        }),
      });

      const data = await resp.json();

      if (!resp.ok || data.error) {
        setError(
          data?.error ||
            "Unable to record this check-in step. Please try again."
        );
        setSubmitting(false);
        return;
      }

      setCheckIn(data.checkIn || {});
      setNextStep(data.nextStep || null);

      if (action === "arrival") {
        setInfoMessage("Arrival recorded.");
      } else if (action === "toys") {
        setInfoMessage("Toy pickup recorded.");
      } else if (action === "photos") {
        setInfoMessage("Photos taken recorded.");
      }

      if (!data.nextStep) {
        setInfoMessage(
          "All check-in steps for this RSVP are now complete."
        );
      }
    } catch (err) {
      console.error("[CheckinPage] handleAdvanceStep error:", err);
      setError("An unexpected error occurred while recording the check-in.");
    } finally {
      setSubmitting(false);
    }
  };

  const arrivalDone = !!checkIn?.arrivalAt;
  const toysDone = !!checkIn?.toysReceivedAt;
  const photosDone = !!checkIn?.photosTakenAt;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f3f4f6",
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 600 }}>
        <CardContent>
          {/* Event banner / header */}
          {eventInfo?.eventImage && (
            <Box
              sx={{
                mb: 2,
                borderRadius: 1.5,
                overflow: "hidden",
                height: 160,
                backgroundImage: `url(${eventInfo.eventImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}

          <Typography variant="h5" gutterBottom>
            {eventInfo?.eventName || "Event Check-in"}
          </Typography>

          {eventInfo?.startDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatDateTime(eventInfo.startDate)}{" "}
              {eventInfo.location ? `• ${eventInfo.location}` : ""}
            </Typography>
          )}

          {!hasRequiredParams && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Missing QR parameters. Please scan a valid event QR code.
            </Alert>
          )}

          {loading && hasRequiredParams && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && infoMessage && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {infoMessage}
            </Alert>
          )}

          {!loading && !error && attendee && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  {attendee.fullName || "Guest"}
                </Typography>
                {attendee.email && (
                  <Typography variant="body2" color="text.secondary">
                    {attendee.email}
                  </Typography>
                )}
                {attendee.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {attendee.phone}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Party Details
                </Typography>
                <Typography variant="body2">
                  Party size: {attendee.partySize ?? "-"}
                </Typography>
                <Typography variant="body2">
                  Adults: {attendee.adultsCount ?? "-"} | Kids:{" "}
                  {attendee.kidsCount ?? "-"}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Check-in Status
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Chip
                    label="Arrived"
                    color={statusColor(arrivalDone)}
                    variant={arrivalDone ? "filled" : "outlined"}
                  />
                  <Chip
                    label="Toys Received"
                    color={statusColor(toysDone)}
                    variant={toysDone ? "filled" : "outlined"}
                  />
                  <Chip
                    label="Photos Taken"
                    color={statusColor(photosDone)}
                    variant={photosDone ? "filled" : "outlined"}
                  />
                </Stack>

                <Box sx={{ mt: 1 }}>
                  {checkIn?.arrivalAt && (
                    <Typography variant="caption" display="block">
                      Arrived:{" "}
                      {formatDateTime(
                        checkIn.arrivalAt.toDate?.() || checkIn.arrivalAt
                      )}
                    </Typography>
                  )}
                  {checkIn?.toysReceivedAt && (
                    <Typography variant="caption" display="block">
                      Toys received:{" "}
                      {formatDateTime(
                        checkIn.toysReceivedAt.toDate?.() ||
                          checkIn.toysReceivedAt
                      )}
                    </Typography>
                  )}
                  {checkIn?.photosTakenAt && (
                    <Typography variant="caption" display="block">
                      Photos taken:{" "}
                      {formatDateTime(
                        checkIn.photosTakenAt.toDate?.() ||
                          checkIn.photosTakenAt
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Action controls */}
              {nextStep && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Next step:{" "}
                    {nextStep === "arrival"
                      ? "Mark family as arrived"
                      : nextStep === "toys"
                      ? "Record toy pickup"
                      : nextStep === "photos"
                      ? "Record photos taken"
                      : nextStep}
                  </Typography>

                  {nextStep === "toys" && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Optional: capture parent signature for receiving toys.
                      </Typography>

                      <TextField
                        label="Parent / Guardian name"
                        fullWidth
                        size="small"
                        sx={{ mb: 1 }}
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                      />

                      <TextField
                        label="Notes"
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        sx={{ mb: 2 }}
                        value={signatureNote}
                        onChange={(e) => setSignatureNote(e.target.value)}
                      />

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Signature
                      </Typography>

                      <Box
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: "#fff",
                        }}
                      >
                        <SignatureCanvas
                          ref={sigPadRef}
                          penColor="black"
                          canvasProps={{
                            width: 540,
                            height: 160,
                            style: { width: "100%", height: "160px" },
                          }}
                        />
                      </Box>

                      <ButtonComponent
                        label="Clear Signature"
                        type="secondary"
                        size="small"
                        onClick={() =>
                          sigPadRef.current && sigPadRef.current.clear()
                        }
                      />
                    </Box>
                  )}

                  <Stack direction="row" spacing={1}>
                    <ButtonComponent
                      label={
                        nextStep === "arrival"
                          ? "Mark Arrived"
                          : nextStep === "toys"
                          ? "Mark Toys Received"
                          : nextStep === "photos"
                          ? "Mark Photos Taken"
                          : "Advance Step"
                      }
                      onClick={() => handleAdvanceStep()}
                      isLoading={submitting}
                      disabled={submitting}
                      size="medium"
                    />

                    {/* Optional: manual override buttons (kept commented) */}
                    {/* <ButtonComponent
                      label="Mark Arrival"
                      onClick={() => handleAdvanceStep("arrival")}
                      disabled={submitting}
                      size="small"
                    />
                    <ButtonComponent
                      label="Mark Toys"
                      onClick={() => handleAdvanceStep("toys")}
                      disabled={submitting}
                      size="small"
                    />
                    <ButtonComponent
                      label="Mark Photos"
                      onClick={() => handleAdvanceStep("photos")}
                      disabled={submitting}
                      size="small"
                    /> */}
                  </Stack>
                </Box>
              )}

              {!nextStep && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  No further steps are required for this RSVP.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CheckinPage;